/**
 * ConfirmDialog - the F-styled confirmation gate for destructive or
 * consequential actions (delete alert, remove user, revoke key, ...).
 * Same dialog recipe as the public CSV-export dialog: radius.panel paper,
 * hairline border, icon tile, primary + quiet button pair.
 */

import React from 'react';
import { Dialog, Box, Typography, Button } from '@mui/material';
import { Icon } from '@iconify/react';
import { useThemeMode, radius } from '../../context/ThemeContext';

function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'danger', // 'danger' | 'primary'
  icon = 'ph:warning',
  hideCancel = false, // single-button informational mode
}) {
  const { palette } = useThemeMode();
  const toneColor = tone === 'danger' ? palette.danger : palette.accent;
  const toneBg = tone === 'danger' ? palette.dangerBg : palette.accentBg;
  const toneHover = tone === 'danger' ? '#C22F2F' : '#1C9550';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: `${radius.panel}px`,
          bgcolor: palette.card,
          border: `1px solid ${palette.cardBorder}`,
          boxShadow: palette.shadowLg,
          maxWidth: 420,
          m: 2,
        },
      }}
    >
      <Box sx={{ p: { xs: 3, sm: 3.5 } }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '12px',
            bgcolor: toneBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <Icon icon={icon} width={22} height={22} color={toneColor} />
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: palette.text, mb: 1 }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: '0.9rem', color: palette.textSecondary, lineHeight: 1.6, mb: 3 }}>
          {description}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
          <Button
            onClick={onConfirm}
            variant="contained"
            disableElevation
            sx={{
              bgcolor: toneColor,
              color: '#fff',
              fontWeight: 600,
              fontSize: '0.86rem',
              textTransform: 'none',
              px: 2.2,
              py: 0.9,
              borderRadius: '10px',
              boxShadow: '0 1px 2px rgba(16,24,40,0.16)',
              '&:hover': { bgcolor: toneHover },
            }}
          >
            {confirmLabel}
          </Button>
          {!hideCancel && (
            <Button
              onClick={onClose}
              sx={{
                color: palette.textSecondary,
                fontWeight: 600,
                fontSize: '0.86rem',
                textTransform: 'none',
                px: 2,
                py: 0.9,
                borderRadius: '10px',
                '&:hover': { bgcolor: palette.surface },
              }}
            >
              {cancelLabel}
            </Button>
          )}
        </Box>
      </Box>
    </Dialog>
  );
}

export default ConfirmDialog;
